import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, XCircle, Package, ArrowRight } from 'lucide-react';
import axios from 'axios';
import { useCart } from '../context/CartContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CheckoutSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('loading');
  const [paymentInfo, setPaymentInfo] = useState(null);
  const { clearCart } = useCart();

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    }
  }, [sessionId]);

  const pollPaymentStatus = async (attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`);
      setPaymentInfo(response.data);

      if (response.data.payment_status === 'paid') {
        setStatus('success');
        clearCart();
        return;
      } else if (response.data.status === 'expired') {
        setStatus('expired');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setStatus('error');
    }
  };

  if (status === 'loading') {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-gold border-t-transparent rounded-full animate-spin mx-auto mb-6" />
          <p className="text-foreground/60">Vérification du paiement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 flex items-center justify-center" data-testid="checkout-success-page">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center max-w-lg px-6"
      >
        {status === 'success' ? (
          <>
            <div className="w-20 h-20 bg-gold/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle size={40} className="text-gold" />
            </div>
            <h1 className="font-heading text-4xl mb-4">Merci pour votre commande !</h1>
            <p className="text-foreground/60 mb-8">
              Votre paiement a été confirmé. Vous recevrez un email de confirmation avec les détails de votre commande.
            </p>
            {paymentInfo && (
              <div className="bg-black/5 p-6 mb-8 text-left border border-black/10">
                <p className="text-sm text-foreground/60 mb-2">Montant payé</p>
                <p className="price-tag text-gold text-2xl">
                  {(paymentInfo.amount_total / 100).toFixed(2)} {paymentInfo.currency?.toUpperCase()}
                </p>
              </div>
            )}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/profile" className="btn-primary flex items-center justify-center gap-2">
                <Package size={16} />
                Voir mes commandes
              </Link>
              <Link to="/shop" className="btn-secondary flex items-center justify-center gap-2">
                Continuer mes achats
                <ArrowRight size={16} />
              </Link>
            </div>
          </>
        ) : (
          <>
            <div className="w-20 h-20 bg-kabyle-red/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle size={40} className="text-kabyle-red" />
            </div>
            <h1 className="font-heading text-4xl mb-4">
              {status === 'expired' ? 'Session expirée' : 'Erreur de paiement'}
            </h1>
            <p className="text-foreground/60 mb-8">
              {status === 'expired' 
                ? 'Votre session de paiement a expiré. Veuillez réessayer.'
                : 'Une erreur est survenue lors du traitement de votre paiement. Veuillez réessayer.'
              }
            </p>
            <Link to="/cart" className="btn-primary inline-flex items-center gap-2">
              Retour au panier
              <ArrowRight size={16} />
            </Link>
          </>
        )}
      </motion.div>
    </div>
  );
};

const CheckoutCancelPage = () => {
  return (
    <div className="min-h-screen pt-24 pb-16 flex items-center justify-center" data-testid="checkout-cancel-page">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center max-w-lg px-6"
      >
        <div className="w-20 h-20 bg-black/10 rounded-full flex items-center justify-center mx-auto mb-6">
          <XCircle size={40} className="text-foreground/60" />
        </div>
        <h1 className="font-heading text-4xl mb-4">Paiement annulé</h1>
        <p className="text-foreground/60 mb-8">
          Votre paiement a été annulé. Votre panier est toujours disponible.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/cart" className="btn-primary flex items-center justify-center gap-2">
            Retour au panier
            <ArrowRight size={16} />
          </Link>
          <Link to="/shop" className="btn-secondary">
            Continuer mes achats
          </Link>
        </div>
      </motion.div>
    </div>
  );
};

export { CheckoutSuccessPage, CheckoutCancelPage };
